import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, LoginOutlined, UserAddOutlined } from "@ant-design/icons";
import type { PaginationProps } from "antd";
import type { ColumnsType, TableRowSelection } from "antd/es/table/interface";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import UserService from "../../services/UserService";
import UserEditModal from "./UserEditModal";
import UserCreateModal from "./UserCreateModal";
import { NavLink, useLocation } from "react-router-dom";
import SettingsRouter from "./SettingsRouter";
import { UserListFilterField } from "./UserListFilterField";
import UserGroupService from "../../services/UserGroupService";
import { can, openNotification } from "../../utils";
import {
  applyAuthTokensAndRedirect,
  captureSession,
  IMPERSONATOR_SESSION_KEY,
  isImpersonating,
} from "../../utils/sessionUtils";

const { Title } = Typography;

interface User {
  id: number;
  userName: string;
  firstName: string;
  lastName: string;
  salesTarget: number;
  gsmPortalEmployeeId: string;
  permissions: {
    id: number;
    userGroupId: number;
    userId: number;
  }[];
  roles: {
    id: number;
    name: string;
    description: string;
    permissions: {
      id: number;
      name: string;
      description: string;
    }[];
  }[];
  profilePhoto: string;
  active: boolean;
  autoAssignment: boolean;
  loginMethod?: "LOCAL" | "GOOGLE" | "GATE";
}

export const UserList: React.FC<any> = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const location = useLocation();
  const lastCurrentPage = location.state?.currentPage ?? 0;
  const lastPageSize = location.state?.pageSize ?? 10;
  const [currentPage, setCurrentPage] = useState<number>(lastCurrentPage);
  const [pageSize, setPageSize] = useState<number>(lastPageSize);
  const [filterText, setFilterText] = useState<string>(
    `page=${lastCurrentPage}&size=${lastPageSize}`
  );
  const lastFormFields = location.state?.formFields ?? {};
  const [formFields, setFormFields] = useState(lastFormFields);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterOptions, setFilterOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loginAsLoadingId, setLoginAsLoadingId] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const roleName = localStorage.getItem("roleName");
  const isAdmin = roleName === "ADMIN";
  const currentUserId = Number(localStorage.getItem("id"));

  const isProtectedUser = (row: User) =>
    row.id === currentUserId || row.roles?.some((role) => role.name === "ADMIN");

  const handleLoginAs = async (userId: number) => {
    setLoginAsLoadingId(userId);
    try {
      localStorage.setItem(IMPERSONATOR_SESSION_KEY, captureSession());
      const response = await UserService.loginAs(userId);
      await applyAuthTokensAndRedirect(response);
    } catch (error: any) {
      localStorage.removeItem(IMPERSONATOR_SESSION_KEY);
      const message = error?.response?.data?.message || "Login As başarısız";
      openNotification("error", "Error", message);
    } finally {
      setLoginAsLoadingId(null);
    }
  };

  const openEditModal = (id: number, row: any) => {
    setSelectedUser(row);
    setSelectedUserId(id);
    setEditModalOpen(true);
  };

  useEffect(() => {
    let newFilterText = filterText;
    newFilterText = newFilterText.replace(/(page=)(\d+)/, `$1${currentPage}`);
    newFilterText = newFilterText.replace(/(size=)(\d+)/, `$1${pageSize}`);
    setFilterText(newFilterText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const roleColors: { [key: string]: string } = {
    ADMIN: "red",
    ACCOUNT_MANAGER: "blue",
    ACCOUNT_MANAGER_MANAGER: "blue",
    HI5_ACM_MANAGER: "cyan",
    HI5_ACM_MEMBER: "cyan",
    SUCCESS_TEAM: "gold",
    SUCCESS_TEAM_MANAGER: "gold",
    SUCCESS_TEAM_REPORTER: "geekblue",
    CALL_TEAM: "purple",
    CALL_TEAM_MANAGER: "purple",
    SUSPEND_TEAM: "magenta",
    SUSPEND_TEAM_MANAGER: "magenta",
    MAS_TEAM_MEMBER: "green",
    MAS_TEAM_MANAGER: "green",
    ACCOUNT_MANAGEMENT_MANAGER: "volcano",
    ACCOUNT_MANAGEMENT_MEMBER: "volcano",
  };

  const getGroups = () => {
    if (can("roles:getAll")) {
      UserGroupService.getAll()
        .then((response: any) => {
          let data = response.data;
          setGroupOptions(data);
        })
        .catch((e: Error) => {
          console.log(e);
        });
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: "Kullanıcı adı",
      dataIndex: "username",
      key: "username",
      ellipsis: true,
      width: 250,
      sorter: true,
    },
    {
      title: "Ad",
      dataIndex: "firstName",
      key: "firstName",
      ellipsis: true,
      width: 150,
      sorter: true,
    },
    {
      title: "Soyad",
      dataIndex: "lastName",
      key: "lastName",
      ellipsis: true,
      width: 150,
      sorter: true,
    },
    {
      title: "Giriş",
      dataIndex: "loginMethod",
      key: "loginMethod",
      width: 110,
      align: "center",
      render: (value: "LOCAL" | "GOOGLE" | "GATE" | undefined) => {
        const labels: Record<string, { text: string; color: string }> = {
          GOOGLE: { text: "Google", color: "blue" },
          GATE: { text: "Gate", color: "purple" },
          LOCAL: { text: "Local", color: "default" },
        };
        const label = labels[value || "LOCAL"] || labels.LOCAL;
        return <Tag color={label.color}>{label.text}</Tag>;
      },
    },
    {
      title: "Roller",
      dataIndex: "roles",
      key: "action",
      ellipsis: true,
      width: 200,
      align: "center",
      render: (_, row) => (
        <>
          {row.roles.map((role: any, index: any) => (
            <div key={index} style={{ margin: "2px 0px" }}>
              <Tag color={roleColors[role.name] || "geekblue"}>
                {role.description}
              </Tag>
            </div>
          ))}
        </>
      ),
    },
    {
      title: "Grup",
      dataIndex: "permissions",
      key: "permissions",
      ellipsis: true,
      width: 200,
      align: "center",
      render: (_, row) => {
        const userGroupIds = row?.permissions?.[0]?.userGroupId ?? null;

        if (!userGroupIds) {
          return <Tag color="gray">Grup yok</Tag>;
        }

        const userGroups = groupOptions.filter(
          (group) => group.id === userGroupIds
        );

        if (userGroups.length === 0) {
          return <Tag color="gray">Grup yok</Tag>;
        }

        return <Tag color="blue">{userGroups[0].groupName}</Tag>;
      },
    },
    {
      title: "Otomatik Atama",
      dataIndex: "autoAssignment",
      key: "autoAssignment",
      width: 150,
      align: "center",
      sorter: true,
      render: (value, row) => (
        <>
          {row.id !== undefined && (
            <Switch
              checked={value}
              onChange={() => handleAutoAssignmentChange(row.id!, value)}
            />
          )}
        </>
      ),
    },
    {
      title: "Durum",
      dataIndex: "active",
      key: "action",
      width: 100,
      align: "center",
      sorter: true,
      render: (value, row) => (
        <>
          {row?.id !== undefined && row?.active === true ? (
            <Popconfirm
              title="Kullanıcı pasife alınacak. Emin misiniz?"
              onConfirm={() => handleChange(row.id!, false)}
              okText="Evet"
              cancelText="Hayır"
              overlayStyle={{ width: 300 }}
            >
              <Switch checked={value} />
            </Popconfirm>
          ) : (
            <Switch
              checked={value}
              onChange={(checked) => {
                if (checked) {
                  handleChange(row.id!, checked);
                }
              }}
            />
          )}
        </>
      ),
    },
    {
      title: "İşlemler",
      key: "actions",
      width: isAdmin && !isImpersonating() ? 280 : 180,
      align: "center",
      render: (_, row) => (
        <Space wrap>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(row.id, row)}
          >
            Düzenle
          </Button>
          {isAdmin && !isImpersonating() ? (
            <Button
              type="link"
              icon={<LoginOutlined />}
              loading={loginAsLoadingId === row.id}
              disabled={!row.active}
              title={row.active ? undefined : "Pasif kullanıcıya Login As yapılamaz"}
              onClick={() => handleLoginAs(row.id)}
            >
              Login As
            </Button>
          ) : null}
          {isAdmin && !isProtectedUser(row) ? (
            <Popconfirm
              title="Kullanıcı silinecek (pasife alınır)"
              description="Kullanıcı pasife alınır ve müşteri atamaları güncellenir."
              okText="Sil"
              cancelText="İptal"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteUsers([row.id])}
            >
              <Button type="link" danger icon={<DeleteOutlined />} loading={deleting}>
                Sil
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection: TableRowSelection<User> | undefined = isAdmin
    ? {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys as number[]),
        getCheckboxProps: (record) => ({
          disabled: isProtectedUser(record),
        }),
      }
    : undefined;

  const deleteUsers = async (userIds: number[]) => {
    if (userIds.length === 0) return;

    setDeleting(true);
    try {
      await Promise.all(userIds.map((id) => UserService.softDelete(id)));
      openNotification(
        "success",
        "Başarılı",
        userIds.length === 1 ? "Kullanıcı silindi" : `${userIds.length} kullanıcı silindi`
      );
      setSelectedRowKeys([]);
      getUsersWithPagination();
    } catch (error: any) {
      const message = error?.response?.data?.message || "Kullanıcı silinemedi";
      openNotification("error", "Hata", message);
    } finally {
      setDeleting(false);
    }
  };

  const getUsersWithPagination = () => {
    setLoading(true);
    UserService.getAllWithPagination(`${filterText}`, roleName)
      .then((response: any) => {
        setUsers(response[0].data);
        setTotalUserCount(response[0].totalElements);
        setSelectedRowKeys([]);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  const paginationOnChange: PaginationProps["onChange"] = (page, pageSize) => {
    if (page > 0) {
      setCurrentPage(page - 1);
    }
    setPageSize(pageSize);
  };

  useEffect(() => {
    getUsersWithPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, roleName, sortField, sortOrder]);

  useEffect(() => {
    getGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilterSort = (field: string, order: any) => {
    const direction =
      order === "ascend" ? "asc" : order === "descend" ? "desc" : "";
    const sortParamRegex = /&sort=([^&]*)&direction=([^&]*)/;
    const sortMatch = filterText.match(sortParamRegex);
    let newFilterText = filterText;
    if (sortMatch) {
      newFilterText = newFilterText.replace(
        sortParamRegex,
        `&sort=${field}&direction=${direction}`
      );
    } else if (field && direction) {
      newFilterText = `${newFilterText}&sort=${field}&direction=${direction}`;
    }
    setFilterText(newFilterText);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let nextSortOrder = "";
    if (sorter.field !== sortField) {
      nextSortOrder = "ascend";
    } else {
      switch (sortOrder) {
        case "ascend":
          nextSortOrder = "descend";
          break;
        case "descend":
          nextSortOrder = "";
          break;
        default:
          nextSortOrder = "ascend";
      }
    }
    setSortOrder(nextSortOrder);
    setSortField(sorter.field || "");
    updateFilterSort(sorter.field, nextSortOrder);
  };

  const handleAutoAssignmentChange = async (id: number, status: boolean) => {
    const res = await UserService.updateAutoAssignmentUserById({
      userId: id,
      autoAssignment: !status,
    });
    if (res.status === 200) {
      getUsersWithPagination();
    }
  };

  const handleChange = async (id: number, status: boolean) => {
    const res = await UserService.updateStatusUserById({
      userId: id,
      active: status,
    });
    if (res.status === 200) {
      getUsersWithPagination();
    }
  };

  return (
    <motion.div
      initial={{ y: 200, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 300, opacity: 0 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 40,
      }}
    >
      <div>
        <Row align={"middle"} justify={"space-between"}>
          <Col span={12}>
            <Title className="onemaz-page-title" level={3}>
              {}
            </Title>
            <Breadcrumb
              items={[
                {
                  title: <NavLink to="/">Home</NavLink>,
                },
                {
                  title: "Settings",
                },
                {
                  title: "Kullanıcılar",
                },
              ]}
            />
          </Col>
          <Col span={12}>
            <Row justify="end" gutter={8}>
              {isAdmin ? (
                <>
                  {selectedRowKeys.length > 0 ? (
                    <Col>
                      <Popconfirm
                        title={`${selectedRowKeys.length} kullanıcı silinecek`}
                        description="Seçili kullanıcılar pasife alınır ve müşteri atamaları güncellenir."
                        okText="Seçilenleri Sil"
                        cancelText="İptal"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteUsers(selectedRowKeys)}
                      >
                        <Button danger icon={<DeleteOutlined />} loading={deleting}>
                          Seçilenleri Sil ({selectedRowKeys.length})
                        </Button>
                      </Popconfirm>
                    </Col>
                  ) : null}
                  <Col>
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={() => setCreateModalOpen(true)}
                    >
                      Yeni Kullanıcı
                    </Button>
                  </Col>
                </>
              ) : null}
              <Col>
                <UserListFilterField
                  setFilterText={setFilterText}
                  pageSize={pageSize}
                  setFormFields={setFormFields}
                  formFields={formFields}
                  setFilterOptions={setFilterOptions}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <SettingsRouter />
        <Card style={{ marginTop: 24 }}>
          <Table
              rowKey={"id"}
              columns={columns}
              dataSource={users}
              key={"id"}
              loading={loading}
              scroll={{ x: 1000 }}
              pagination={false}
              rowSelection={rowSelection}
              onChange={handleTableChange}
            />
          <Row style={{ marginTop: 24 }} justify="center">
            <Pagination
              showSizeChanger={true}
              total={totalUserCount}
              defaultPageSize={pageSize}
              defaultCurrent={0}
              current={currentPage + 1}
              showQuickJumper
              onChange={paginationOnChange}
            />
          </Row>
        </Card>
      </div>
      <UserCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={getUsersWithPagination}
      />
      <UserEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        userId={selectedUserId}
        userData={selectedUser}
        onSuccess={getUsersWithPagination}
      />
    </motion.div>
  );
};
